import { v4 } from "uuid";

class FormulaNode {
  constructor(type, meta) {
    this.type = type;
    this.meta = meta;
    this.id = v4();
    this.children = [];
    this.leftContent = null;
    this.rightContent = null;
  }
}

class FormulaTree {
  static NODE_TYPES = {
    Function: "Function",
    Operator: "Operator",
    Value: "Value",
    Constants: "Constants",
    Variable: "Variable",
    Brackets: "Brackets",
  };

  constructor(updater) {
    this.tree = null;
    this._updater = updater;
  }

  _createNode({ type, ...meta }) {
    return new FormulaNode(type, meta);
  }

  _updateChild(parentNode, options, newNode) {
    if (
      parentNode.type === FormulaTree.NODE_TYPES.Function ||
      parentNode.type === FormulaTree.NODE_TYPES.Brackets
    ) {
      const currentChild = parentNode.children[options.currentChildIndex];
      parentNode.children[options.currentChildIndex] = currentChild
        ? this._insertNode(currentChild, newNode, options)
        : { ...newNode, parentId: parentNode.id };

      return parentNode;
    }

    if (parentNode.type === FormulaTree.NODE_TYPES.Operator) {
      parentNode.rightContent = { ...newNode, parentId: parentNode.id };
    }

    return parentNode;
  }

  _insertNode(parentNode, newNode, options) {
    const { type } = newNode;

    switch (type) {
      case FormulaTree.NODE_TYPES.Operator:
        const temp = {...parentNode};
        const tempId = temp.id;
        const tempParentId = temp?.parentId ?? tempId;
        const tempNewNodeId = newNode.id;
        newNode.id = tempId;
        newNode.parentId = tempParentId
        newNode.leftContent = {
          ...parentNode,
          id: tempNewNodeId,
          parentId: tempId,
        };
        parentNode = newNode
        break;
      case FormulaTree.NODE_TYPES.Function:
      case FormulaTree.NODE_TYPES.Value:
      case FormulaTree.NODE_TYPES.Constants:
      case FormulaTree.NODE_TYPES.Variable:
      case FormulaTree.NODE_TYPES.Brackets:
        parentNode = this._updateChild(parentNode, options, newNode);

        break;
      default:
        break;
    }

    return parentNode;
  }

  setNode({ item, currentNodeId, ...restOptions }) {
    const newNode = this._createNode(item);
    if (this.isEmpty()) {
      this._setTree(newNode);
      return;
    }
    const parentNode = this.findNodeById(this.tree, currentNodeId);
    const updatedNode = this._insertNode(parentNode, newNode, restOptions);
    this._updateNode(parentNode, updatedNode);
  }

  updatedCurrentNode({ item, currentNodeId, ...restOptions }) {
    const newNode = this._createNode(item);
    const currentNode = this.findNodeById(this.tree, currentNodeId);

    newNode.leftContent = currentNode.leftContent;
    newNode.rightContent = currentNode.rightContent;
    newNode.id = currentNode.id;
    newNode.parentId = currentNode?.parentId;

    this._updateNode(currentNode, newNode);
  }

  setNewTree(tree) {
    this.tree = tree;
    this._updater(this.tree, FormulaTree.getNodeList(this.tree));
  }

  findNodeById(node, id) {
    if (!node) return;
    if (node.id === id) return node;

    for (let child of node.children) {
      const foundNode = this.findNodeById(child, id);

      if (foundNode) {
        return foundNode;
      }
    }

    return (
      this.findNodeById(node.leftContent, id) ||
      this.findNodeById(node.rightContent, id)
    );
  }

  _updateNode(prevNode, newNode) {
    Object.keys(prevNode).forEach((key) => {
      prevNode[key] = newNode[key];
    });

    this._setTree(this.tree);
  }

  _setTree(tree) {
    this.tree = tree;
    this._updater(this.tree, FormulaTree.getNodeList(this.tree));
  }

  isEmpty() {
    return !this.tree;
  }

  static getNodeList(currentNode, nodeList = []) {
    if (!currentNode) return [];

    FormulaTree.getNodeList(currentNode.leftContent, nodeList);
    nodeList.push({
      ...currentNode.meta,
      children: currentNode.children,
      parentId: currentNode.id,
      id: currentNode.id,
      type: currentNode.type,
    });

    FormulaTree.getNodeList(currentNode.rightContent, nodeList);

    return nodeList;
  }

  createStringExpression() {
    const buildString = (node) => {
      let str = "";
      if (node?.label) {
        str += node.label;
      }
      if (node.leftContent) {
        str += buildString(node.leftContent);
      }
      if (node.rightContent) {
        str += buildString(node.rightContent);
      }
      if (node?.children?.length > 0) {
        node?.children.forEach((childNode, index) => {
          const nodeChildeList = FormulaTree.getNodeList(childNode);
          nodeChildeList.forEach((nodeChildeDeep) => {
            str += buildString(nodeChildeDeep);
          });
          if (
            node.type === FormulaTree.NODE_TYPES.Function &&
            node?.children?.length > 1 &&
            index + 1 !== node?.children?.length
          ) {
            str += ",";
          }
        });
        str += ")";
      }
      return str;
    };

    const nodeList = FormulaTree.getNodeList(this.tree);

    let result = "";
    nodeList.forEach((node) => {
      result += buildString(node);
    });

    return result;
  }

  isValidTree(tree, isValid = true) {
    const isNeedChildren =
      tree.type === FormulaTree.NODE_TYPES.Brackets ||
      tree.type === FormulaTree.NODE_TYPES.Function;

    const isNeedLeftAndRightContent =
      tree.type === FormulaTree.NODE_TYPES.Operator;

    if (
      isNeedChildren &&
      (!tree.children ||
        tree.children.length === 0 ||
        tree.children.length !== tree.meta.argumentsCount.length)
    ) {
      return false;
    }

    if (
      isNeedLeftAndRightContent &&
      (!tree.leftContent || !tree.rightContent)
    ) {
      return false;
    }

    const childResults = [];
    for (let child of tree.children) {
      const childValid = this.isValidTree(child);
      childResults.push(childValid);
    }

    if (childResults.includes(false)) {
      return false;
    }

    const leftValid = tree.leftContent
      ? this.isValidTree(tree.leftContent)
      : true;
    const rightValid = tree.rightContent
      ? this.isValidTree(tree.rightContent)
      : true;

    return leftValid && rightValid && isValid;
  }

  deleteNode(id) {
    const currentNode = this.findNodeById(this.tree, id);
    const parentNode = this.findNodeById(this.tree, currentNode?.parentId) ?? this.tree;

    const handleDelete = (currentNode, parentNode) => {
      let newParent;
      if (currentNode.type === FormulaTree.NODE_TYPES.Operator) {
        newParent = currentNode.leftContent;
        newParent.parentId = parentNode.id ?? newParent.id
        newParent.rightContent = null
        return newParent;
      }
      if (parentNode?.leftContent?.id === currentNode?.id) {
        const tempParentNode = {...parentNode}
        newParent = parentNode?.rightContent;
        newParent.parentId = tempParentNode.parentId ?? newParent.id
        return newParent;
      }
      if (parentNode?.rightContent?.id === currentNode?.id) {
        const tempParentNode = {...parentNode}
        newParent = parentNode?.leftContent;
        newParent.parentId = tempParentNode.parentId ?? newParent.id
        return newParent;
      }

    };

    const newParentNode = handleDelete(currentNode, parentNode);

    if (!newParentNode && parentNode?.type === FormulaTree.NODE_TYPES.Function) {
      const filteredNode = parentNode.children.filter((item) => {
         return item.id  !== currentNode.id
      })

      const newParent = {
        ...parentNode,
        children: filteredNode,
      }

      if (parentNode.id === currentNode.id) {
        this._setTree(null)
      }else {
        this._updateNode(parentNode, newParent);
      }
      return 
    }
    if (!newParentNode) {
      this._setTree(null)
    }
    else {
      if (parentNode?.type === FormulaTree.NODE_TYPES.Function) {
        const filteredNode = parentNode.children.map((item) => {
            if (item.id === currentNode.id) {
              return newParentNode
            }else {
              return item
            }
        })
  
        const newParent = {
          ...parentNode,
          children: filteredNode,
        }
  
        this._updateNode(parentNode, newParent);
      }else{
        this._updateNode(parentNode, newParentNode);
      }

    }
  }

  clearTree() {
    this.tree = null;
  }
}

export default FormulaTree;
